// Leaf module (no imports) so the scheduler can read a view key without loading the grid definitions.

// A related-inventory view saved from a project detail page prefixes its name with the project's
// UUID, so each project keeps its own set. Account-wide views (the Projects page and every legacy
// view) have no prefix. `name` is the portion after the `related_project_assets_grid_` prefix.
const PROJECT_SCOPED_NAME = /^([0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12})_(.+)$/;

export const parseProjectScopedViewName = (name: string): { projectId: string; displayName: string } | null => {
  const match = name.match(PROJECT_SCOPED_NAME);
  return match ? { projectId: match[1], displayName: match[2] } : null;
};

export const buildProjectScopedName = (projectId: string, displayName: string): string => `${projectId}_${displayName}`;

export const RELATED_INVENTORY_TYPE = 'related_project_assets';

// Only related-inventory views are per-project; their saved-view name carries the project UUID as a
// prefix. Every other type keeps its name verbatim, so its scope must never be inferred from the name.
export const isProjectScopedType = (type: string): boolean => type === RELATED_INVENTORY_TYPE;

// An account-wide related-inventory view whose name parses as project-scoped would be reclassified onto a
// project's card (scope for that type is read from the name). Reserve that shape on both create and rename.
export const isReservedAccountWideName = (type: string, projectId: string | undefined, name: string): boolean =>
  !projectId && isProjectScopedType(type) && parseProjectScopedViewName(name) !== null;

export const viewTypeOf = (viewKey: string): string => viewKey.split('_grid_')[0];

// What a view is called when no list of saved views says: its key without the table and, for a per-project view, the
// project it belongs to.
export const viewNameFromKey = (viewKey: string): string => {
  const [type, ...rest] = viewKey.split('_grid_');
  const name = rest.join('_grid_');
  return isProjectScopedType(type) ? (parseProjectScopedViewName(name)?.displayName ?? name) : name;
};

// The narrative is an account-level projects overview, offered above the projects table and its
// related-inventory counterpart; both attach the latest account AI summary.
const SUMMARY_VIEW_PREFIXES = ['project', RELATED_INVENTORY_TYPE];
export const offersExecutiveSummary = (viewKey: string): boolean => {
  const [prefix, ...rest] = viewKey.split('_grid_');
  if (!SUMMARY_VIEW_PREFIXES.includes(prefix)) return false;
  // Only a per-project related-inventory report suppresses the summary; a project-type view merely named
  // like one keeps it, since its name is not a scope. The narrative is an account-level projects overview —
  // the wrong altitude for a single project, and possibly the wrong account for internal staff.
  if (!isProjectScopedType(prefix)) return true;
  return parseProjectScopedViewName(rest.join('_grid_')) === null;
};
